import React, { useRef } from 'react';

/**
 * SegmentedControl
 * Rounded-pill group for choosing between a small set of mutually-exclusive options.
 *
 * Props:
 *   options: Array<{ value: string, label: string, ariaLabel?: string }>
 *   value: string — currently selected value
 *   onChange: (value) => void
 *   size: 'sm' | 'md' (default 'md')
 *   ariaLabel: string — accessible label for the group (role=radiogroup)
 *   className: string — extra classes on the outer container
 */
export default function SegmentedControl({
  options,
  value,
  onChange,
  size = 'md',
  ariaLabel,
  className = ''
}) {
  const buttonsRef = useRef([]);

  const sizeClasses = size === 'sm'
    ? 'text-xs py-1 px-2.5'
    : 'text-sm py-1.5 px-3';

  const handleKeyDown = (e, idx) => {
    if (e.key !== 'ArrowLeft' && e.key !== 'ArrowRight' && e.key !== 'Home' && e.key !== 'End') {
      return;
    }
    e.preventDefault();
    let nextIdx = idx;
    if (e.key === 'ArrowLeft') nextIdx = (idx - 1 + options.length) % options.length;
    else if (e.key === 'ArrowRight') nextIdx = (idx + 1) % options.length;
    else if (e.key === 'Home') nextIdx = 0;
    else if (e.key === 'End') nextIdx = options.length - 1;

    const next = options[nextIdx];
    onChange(next.value);
    const btn = buttonsRef.current[nextIdx];
    if (btn) btn.focus();
  };

  return (
    <div
      role="radiogroup"
      aria-label={ariaLabel}
      className={`inline-flex bg-gray-100 rounded-full p-0.5 overflow-x-auto max-w-full ${className}`}
    >
      {options.map((opt, idx) => {
        const active = opt.value === value;
        return (
          <button
            key={opt.value}
            ref={el => (buttonsRef.current[idx] = el)}
            type="button"
            role="radio"
            aria-checked={active}
            aria-label={opt.ariaLabel || opt.label}
            tabIndex={active ? 0 : -1}
            onClick={() => onChange(opt.value)}
            onKeyDown={e => handleKeyDown(e, idx)}
            className={[
              'whitespace-nowrap rounded-full font-medium transition-colors',
              'focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-1',
              sizeClasses,
              active
                ? 'bg-white shadow-sm text-gray-900'
                : 'text-gray-600 hover:text-gray-900'
            ].join(' ')}
          >
            {opt.label}
          </button>
        );
      })}
    </div>
  );
}
